/**
 * Module dependencies.
 */

import { Combobox } from 'app/components/ui/combobox';
import { ErrorField } from '../../error-field/error-field';
import { FormField } from '../../form-field/form-field';
import React, { forwardRef } from 'react';

/**
 * `Option` type.
 */

type Option = {
  value: string | number;
  label: React.ReactNode;
};

/**
 * Export `SelectProps` type.
 *
 * Mirrors the antd Select prop surface still used across the app; the options
 * are honoured by the Combobox (searchable/multiple/allowClear/loading/filter).
 */

export type SelectProps = {
  error?: any;
  className?: string;
  disabled?: boolean;
  fieldClassName?: string;
  invalid?: boolean;
  minimal?: boolean;
  isDirty?: boolean;
  isTouched?: boolean;
  hideError?: boolean;
  label?: string;
  name: string;
  onChange?: (value: any) => void;
  options?: Option[];
  placeholder?: string;
  value?: any;
  mode?: 'multiple' | 'tags';
  allowClear?: boolean;
  showSearch?: boolean;
  filterOption?: boolean | ((input: string, option?: any) => boolean);
  optionFilterProp?: string;
  optionLabelProp?: string;
  defaultValue?: any;
  onSearch?: (value: string) => void;
  onBlur?: any;
  onFocus?: any;
  style?: React.CSSProperties;
  size?: string;
  loading?: boolean;
  notFoundContent?: React.ReactNode;
};

const HackFragment = (props: any) => {
  return props.children;
};

/**
 * `SelectComponent` component.
 */

function SelectComponent(props: SelectProps, forwardedRef: any): JSX.Element {
  const {
    allowClear,
    className,
    disabled,
    error,
    fieldClassName,
    filterOption,
    hideError,
    invalid,
    isTouched,
    label,
    loading,
    minimal,
    mode,
    onChange,
    optionLabelProp,
    options = [],
    placeholder,
    showSearch,
    value
  } = props;

  const FormFieldComponent = minimal ? HackFragment : FormField;

  return (
    <FormFieldComponent className={className} label={label}>
      <Combobox
        allowClear={allowClear}
        disabled={disabled}
        filterOption={typeof filterOption === 'function' ? filterOption : undefined}
        loading={loading}
        multiple={mode === 'multiple' || mode === 'tags'}
        onChange={nextValue => onChange?.(nextValue)}
        optionLabelProp={optionLabelProp}
        options={options}
        placeholder={placeholder}
        ref={forwardedRef}
        searchable={!!showSearch}
        triggerClassName={fieldClassName}
        value={value}
      />

      {!hideError && <ErrorField error={error} isVisible={invalid || isTouched} />}
    </FormFieldComponent>
  );
}

/**
 * Export `Select` component.
 */

export const Select = forwardRef<any, SelectProps>(SelectComponent);
